import React, { useRef, useState } from 'react';
import { useCompanySearch } from '../hooks/useCompanySearch';
import styles from './PaymentInvoices.module.css';

// ─── Sub-component ────────────────────────────────────────────────────────────

const CompanyOption = ({ company, isSelected, onActivate }) => (
  <li role="option" aria-selected={isSelected}>
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 12px',
        cursor: 'pointer',
        fontSize: 13,
        background: isSelected ? '#f0fbff' : 'none',
        boxSizing: 'border-box',
        color: 'var(--ink)',
      }}
      onMouseDown={e => {
        e.preventDefault();
        onActivate(company);
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = '#00aeef';
        e.currentTarget.style.color = '#fff';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = isSelected ? '#f0fbff' : 'none';
        e.currentTarget.style.color = 'var(--ink)';
      }}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {}}
        style={{ flexShrink: 0, accentColor: '#00aeef', cursor: 'pointer' }}
      />
      <span>
        {company.name}
        {company.city && (
          <div style={{ fontSize: '0.8em', opacity: 0.75 }}>{company.city}</div>
        )}
      </span>
    </label>
  </li>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CompanySearchSelect = ({
  onSelect,
  onToggle,
  selectedCompanies,
  multiSelect = false,
  placeholder = 'Search companies...',
  service,
  scheduler,
}) => {
  const {
    query,
    isLoading,
    hasResults,
    isEmpty,
    companies,
    setQuery,
    reset,
  } = useCompanySearch(service, scheduler);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedIds = new Set((selectedCompanies || []).map(c => c.companyId));

  const handleChange = e => {
    setQuery(e.target.value);
    setOpen(true);
  };

  const handleActivate = company => {
    if (multiSelect) {
      onToggle(company);
    } else {
      reset();
      setOpen(false);
      onSelect(company);
    }
  };

  const handleBlur = e => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget)) {
      setOpen(false);
    }
  };

  const ALL_COMPANIES = { companyId: 'ALL', name: 'All Companies' };
  const showDropdown = open;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }} onBlur={handleBlur}>
      {multiSelect && selectedCompanies && selectedCompanies.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {selectedCompanies.map(c => (
            <span
              key={c.companyId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 8px',
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 12,
                fontSize: 12,
                color: 'var(--ink)',
              }}
            >
              {c.name}
              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  onToggle(c);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#999',
                  padding: 0,
                  fontSize: 11,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        className={styles.formInput}
        style={{ width: '100%' }}
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
      />
      {showDropdown && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 20,
            background: '#fff',
            border: '1px solid var(--bd)',
            borderRadius: 6,
            maxHeight: 200,
            overflowY: 'auto',
            boxShadow: 'var(--sh2)',
            margin: 0,
            padding: 0,
            listStyle: 'none',
          }}
        >
          <CompanyOption
            company={ALL_COMPANIES}
            isSelected={selectedIds.has('ALL')}
            onActivate={handleActivate}
          />
          {isLoading && (
            <li
              style={{
                padding: '8px 12px',
                color: 'var(--ink3)',
                fontSize: 13,
              }}
            >
              Loading...
            </li>
          )}
          {isEmpty && !isLoading && (
            <li
              style={{
                padding: '8px 12px',
                color: 'var(--ink3)',
                fontSize: 13,
              }}
            >
              No results
            </li>
          )}
          {hasResults &&
            !isLoading &&
            companies.map(company => (
              <CompanyOption
                key={company.companyId}
                company={company}
                isSelected={selectedIds.has(company.companyId)}
                onActivate={handleActivate}
              />
            ))}
        </ul>
      )}
    </div>
  );
};

export default CompanySearchSelect;
